import camelize from 'camelize';
import isEmpty from 'lodash/isEmpty';
import { batch } from 'react-redux';

import {
  AudioPlayerActions,
  BattlecardActions,
  BookmarkActions,
  CoachingActions,
  CopilotActions,
  MeetingActions,
  MeetingTranslationActions,
  RealtimeAssistantActions,
  RealtimeTranscriptFetchActions,
  ScorecardsActions,
  WebsocketActions
} from 'actions/actionTypes';
import { readCopilotOmnisearchRequestIdFromWsData } from 'actions/copilotOmnisearch';
import {
  fetchCurrentlySelectedMeeting,
  fetchMeetingMeta,
  setRefetchNeeded
} from 'actions/meetings';
import { trackSegmentEvents } from 'actions/segment';
import { fetchTranscription } from 'actions/transcriptions';

import {
  getCopilotStreamDataJsonContextUuids,
  sanitizeCopilotStreamMentions
} from 'helpers/copilot';
import { completeCopilotCancellationMessages } from 'helpers/copilotCancellation';
import {
  createCopilotStreamChunkQueue,
  getCopilotStreamChunkMessageUuid
} from 'helpers/copilotStreamChunkQueue';
import {
  buildCopilotRunEventsUrl,
  cancelCopilotRun as cancelCopilotRunRequest,
  connectCopilotRun,
  createCopilotRunEventDeduper,
  getCopilotChunkWatchdogDelayMs,
  getCopilotRunUuid,
  getCopilotStreamChatUuid,
  getCopilotStreamMessageData,
  hasCopilotRunStreamProgress,
  isActiveCopilotRunStatus,
  loadStoredCopilotRuns,
  normalizeCancelledRunPayload,
  removeStoredCopilotRun,
  saveStoredCopilotRun,
  startCopilotRun
} from 'helpers/copilotStreamClient';
import { shouldUseCopilotSseStreamForFlags } from 'helpers/copilotTransport';
import { camelizeCopilotPayload } from 'helpers/copilotAgentBuilderDraftIngestion';
import env from 'helpers/env';
import getLastUrlParam from 'helpers/getLastUrlParam';
import { get } from 'helpers/http';
import { L10nCopilot } from 'helpers/l10n/L10nCopilot';
import logError from 'helpers/logError';
import {
  getMeetingMetaStateKey,
  hasUsableTranscriptionContent,
  isAudioPlaybackReadyMeetingMetadata,
  shouldRefreshMeetingContentAfterMetaUpdate
} from 'helpers/meetingPostProcessingRefresh';

import { COPILOT_ERROR_MESSAGE, MESSAGE_AUTHOR_TYPES } from 'constants/copilot';

const HTTP_CONFLICT_ERROR_NAME = 'Conflict';

const COPILOT_OMNISEARCH_CATEGORY_KEYS = [
  'meeting',
  'deal',
  'company',
  'account',
  'playlist',
  'user',
  'team'
];

const isOmnisearchResultEnvelope = value =>
  value &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Array.isArray(value.results);

const isNewOmnisearchDataShape = data =>
  isOmnisearchResultEnvelope(data) ||
  (data &&
    typeof data === 'object' &&
    COPILOT_OMNISEARCH_CATEGORY_KEYS.some(key =>
      isOmnisearchResultEnvelope(data[key])
    ));

const normalizeOmnisearchSearchType = searchType => {
  switch (searchType) {
    case 'meeting':
      return 'meeting';
    case 'deal':
      return 'deal';
    case 'company':
    case 'account':
      return 'company';
    case 'playlist':
      return 'playlist';
    case 'user':
      return 'user';
    case 'team':
      return 'team';
    default:
      return null;
  }
};

/**
 * Backoff for restoring a dropped connection. A meeting page subscribes once on
 * mount, so without this the socket stays closed and `meeting_meta` never
 * arrives again — the page looks frozen until a manual reload.
 */
const WEBSOCKET_RECONNECT_DELAYS_MS = [1000, 2000, 5000, 10000, 30000];

const getSubscriptionKey = ({ streamType, entityKey, uuid }) =>
  `${streamType}|${entityKey}|${uuid}`;

/**
 * Post-recording refresh has no UI of its own: when it fails the page just looks
 * stale. These breadcrumbs are what separates "backend never published the
 * state" from "the frontend ignored it", so they stay on in every build. Volume
 * is a handful of lines per meeting.
 */
const logWebsocketDiagnostic = (event, details) => {
  // eslint-disable-next-line no-console
  console.info(`[avoma-ws] ${event}`, details);
};

/**
 * Normalizes both omnisearch response contracts into the existing reducer shape.
 * The new backend wraps each category in `{ count, has_next, results }`, while
 * the current backend returns category arrays directly.
 */
export const normalizeCopilotOmnisearchResults = (data, searchType) => {
  const results = {};
  const resultMetadata = {};

  const addCategory = (categoryKey, value) => {
    if (Array.isArray(value)) {
      results[categoryKey] = value;
      return;
    }
    if (!isOmnisearchResultEnvelope(value)) {
      return;
    }
    results[categoryKey] = value.results;
    const hasNext = value.hasNext ?? value.has_next;
    let count = null;
    if (typeof value.count === 'number') {
      count = value.count;
    } else if (typeof value.count === 'string' && value.count.trim() !== '') {
      count = Number(value.count);
    }
    resultMetadata[categoryKey] = {
      count: Number.isFinite(count) ? count : null,
      hasNext: typeof hasNext === 'boolean' ? hasNext : null
    };
  };

  const directCategory = normalizeOmnisearchSearchType(searchType);
  if (directCategory && isOmnisearchResultEnvelope(data)) {
    addCategory(directCategory, data);
  } else if (data && typeof data === 'object') {
    COPILOT_OMNISEARCH_CATEGORY_KEYS.forEach(key => {
      const categoryKey = key === 'account' ? 'company' : key;
      if (key === 'account' && results.company?.length) {
        return;
      }
      addCategory(categoryKey, data[key]);
    });
  }

  return {
    ...results,
    resultMetadata,
    contextTypeOrder: data?.contextTypeOrder
  };
};

/**
 * Returns true for grouped legacy arrays and the new grouped/category result envelopes.
 * Paginated responses can omit `message_type`, so both `results` and nested category
 * envelopes are part of the fallback shape check.
 */
export const isCopilotOmnisearchResponse = payload =>
  payload?.messageType === 'omnisearch' ||
  (isEmpty(payload?.dbObject) &&
    payload?.data &&
    (isNewOmnisearchDataShape(payload.data) ||
      Array.isArray(payload.data.meeting) ||
      Array.isArray(payload.data.deal) ||
      Array.isArray(payload.data.user) ||
      Array.isArray(payload.data.team) ||
      Array.isArray(payload.data.playlist) ||
      Array.isArray(payload.data.company) ||
      Array.isArray(payload.data.account)));

/**
 * Finite numeric `count` from an omnisearch payload, tolerating the numeric-string form the
 * backend sometimes sends (the grouped envelope path in {@link normalizeCopilotOmnisearchResults}
 * already coerces string counts, so the paginated guard must accept them too). Returns `null` when
 * `count` is absent or non-numeric.
 */
export const readOmnisearchResultCount = value => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/**
 * Routes a `TYPE_COPILOT` omnisearch response to the primary grouped slot vs. a single-category
 * "Load more" page. A paginated `{count, results}` response echoes no `request_id`/`search_type`
 * and is structurally identical to a primary single-`search_type` response, so shape alone cannot
 * tell them apart. It is treated as paginated only when a page is pending
 * (`SEND_COPILOT_OMNISEARCH` clears that queue) and no primary is in flight — for a response racing
 * that clear we prefer the primary while it is still loading, because mis-attributing a primary
 * strands `contextOmnisearch.loading` at `true`, whereas a dropped page only leaves the "Load more"
 * row in place.
 * @param {object} payload Camelized WS payload.
 * @param {object} copilotState `store.getState().copilot`.
 * A response with no `request_id` that arrives when neither a primary nor a page is pending is an
 * orphaned page (e.g. its pending key was cleared by a newer search); it is flagged so it can be
 * dropped instead of overwriting the primary results and cache.
 * @returns {{ isOmnisearchResponse: boolean, isPrimaryOmnisearchResponseInFlight: boolean, isPaginatedOmnisearchResponse: boolean, isOrphanedOmnisearchResponse: boolean }}
 */
export const classifyCopilotOmnisearchResponse = (payload, copilotState) => {
  const isOmnisearchResponse = isCopilotOmnisearchResponse(payload);
  const echoedOmnisearchRequestId = readCopilotOmnisearchRequestIdFromWsData(
    payload?.data
  );
  const activePrimaryOmnisearchRequestId =
    copilotState?.contextOmnisearch?.activeRequestId ?? null;
  const isPrimaryOmnisearchResponseInFlight =
    Boolean(copilotState?.contextOmnisearch?.loading) &&
    (echoedOmnisearchRequestId == null ||
      activePrimaryOmnisearchRequestId == null ||
      String(echoedOmnisearchRequestId) ===
        String(activePrimaryOmnisearchRequestId));
  const hasPendingPage =
    (copilotState?.contextOmnisearchLoadMorePendingKeys?.length ?? 0) > 0;
  const isPaginatedOmnisearchResponse =
    isOmnisearchResponse &&
    hasPendingPage &&
    !isPrimaryOmnisearchResponseInFlight &&
    Boolean(payload?.data) &&
    Array.isArray(payload.data.results) &&
    (readOmnisearchResultCount(payload.data.count) != null ||
      typeof payload.data.hasNext === 'boolean');
  const isOrphanedOmnisearchResponse =
    isOmnisearchResponse &&
    echoedOmnisearchRequestId == null &&
    !isPrimaryOmnisearchResponseInFlight &&
    !hasPendingPage;
  return {
    isOmnisearchResponse,
    isPrimaryOmnisearchResponseInFlight,
    isPaginatedOmnisearchResponse,
    isOrphanedOmnisearchResponse
  };
};

const getCopilotMessageDataJsonForAction = action => {
  const contextPayload =
    getCopilotStreamDataJsonContextUuids(
      action.contextType,
      action.contextUuid,
      action.additionalContextMentions,
      action.primaryContextLabel
    ) || {};

  return {
    ...contextPayload,
    ...(contextPayload.mentions
      ? { mentions: sanitizeCopilotStreamMentions(contextPayload.mentions) }
      : {}),
    ...(action.userPromptUuid && {
      user_prompt_uuid: action.userPromptUuid
    }),
    ...(action.webSearchEnabled ? { tools: ['search_web'] } : {}),
    ...(action.attachmentUuids?.length
      ? { attachment_uuids: action.attachmentUuids }
      : {}),
    ...(action.agentDraft ? { agent: action.agentDraft } : {})
  };
};

const getMessageForAction = (store, action, wsAction) => {
  if (
    action.streamType === WebsocketActions.TYPE_COPILOT &&
    wsAction === 'send'
  ) {
    const contextPayload =
      getCopilotStreamDataJsonContextUuids(
        action.contextType,
        action.contextUuid,
        action.additionalContextMentions,
        action.primaryContextLabel,
        action.primaryContextWireExtras
      ) || {};
    const dataJson = {
      ...contextPayload,
      ...(contextPayload.mentions
        ? { mentions: sanitizeCopilotStreamMentions(contextPayload.mentions) }
        : {}),
      ...(action.userPromptUuid && {
        user_prompt_uuid: action.userPromptUuid
      }),
      ...(action.webSearchEnabled ? { tools: ['search_web'] } : {}),
      ...(action.attachmentUuids?.length
        ? { attachment_uuids: action.attachmentUuids }
        : {}),
      ...(action.agentDraft ? { agent: action.agentDraft } : {})
    };
    return JSON.stringify({
      msg: 'stream',
      stream: action.streamType,
      user_email: store.getState().user.email,
      db_object: {
        uuid: action.chatUuid,
        entity_key: action.entityKey
      },
      data: {
        message: action.message,
        data_json: dataJson
      }
    });
  }

  return JSON.stringify({
    msg: 'stream',
    stream: action.streamType,
    action: wsAction,
    db_object: {
      uuid: action.uuid,
      entity_key: action.entityKey
    },
    meta: {
      ts: new Date().toISOString(),
      user_email: store.getState().user.email
    }
  });
};

const dispatchOptimisticCopilotUserMessage = (
  store,
  messageAction,
  clientRequestId,
  runUuid
) => {
  store.dispatch({
    type: CopilotActions.RECEIVE_COPILOT_MESSAGE,
    data: {
      uuid: null,
      author: 1,
      message: messageAction.message,
      chatUuid: messageAction.chatUuid,
      clientRequestId,
      copilotRunUuid: runUuid,
      created: messageAction.clientCreatedAt || new Date().toISOString(),
      ...(messageAction.copilotStandaloneContextPills?.length
        ? {
            copilotStandaloneContextPills:
              messageAction.copilotStandaloneContextPills
          }
        : {}),
      ...(messageAction.attachments?.length
        ? { attachments: messageAction.attachments }
        : {})
    }
  });
};

const getOmnisearchSocketPayload = (store, action) => {
  const data = {
    query: action.query,
    request_id: action.requestId
  };
  if (action.searchType) {
    data.search_type = action.searchType;
  }
  /** "Load more": backend requires `search_type` (set above) whenever `page` is sent. */
  if (action.page != null) {
    data.page = action.page;
    if (action.pageSize != null) {
      data.page_size = action.pageSize;
    }
  } else if (action.pageSize != null) {
    data.page_size = action.pageSize;
  }
  return JSON.stringify({
    msg: 'stream',
    stream: WebsocketActions.TYPE_COPILOT,
    user_email: store.getState().user.email,
    message_type: 'omnisearch',
    data
  });
};

/**
 * Backend `meeting_meta` publishes `audio_ready` (or a later playback-ready
 * state) after audio upload is acked. Re-fetch the meeting and bump
 * AUDIO_PLAYER_REFRESH once a recording URL is known so Recording loads media
 * from the socket signal.
 *
 * Important: HTTP `fetchMeetingMeta` can store `audio_ready` before the socket
 * frame arrives. In that case there is no meta *transition*, but media may still
 * be missing — still refresh the player so long uploads are not stuck until a
 * hard reload.
 */
/** Dedupes missing-media AUDIO_PLAYER_REFRESH to once per meeting+recording URL. */
let lastMissingMediaAudioRefreshKey = null;

/** @internal test helper */
export const resetMeetingMetaAudioRefreshGuardForTests = () => {
  lastMissingMediaAudioRefreshKey = null;
};

const missingMediaAudioRefreshKey = (meetingUuid, recordingUrl) =>
  `${meetingUuid}:${recordingUrl || ''}`;

const refreshMeetingContentFromMeta = (store, meetingUuid, metadata) => {
  const state = store.getState();
  const isCurrentMeeting = state.app?.currentMeetingId === meetingUuid;
  const previousMetadata = state.meetings?.metadata?.[meetingUuid];
  const meeting = state.meetings?.data?.[meetingUuid];
  const playbackReady = isAudioPlaybackReadyMeetingMetadata(metadata);
  const becameReady = shouldRefreshMeetingContentAfterMetaUpdate(
    previousMetadata,
    metadata
  );

  const recordingUrl = meeting?.recording;
  const recordingId = recordingUrl ? getLastUrlParam(recordingUrl) : null;
  const storedRecording = recordingId
    ? state.recordings?.data?.[recordingId]
    : null;
  const hasRecordingMedia = Boolean(storedRecording?.recordingFile);
  const needsPlayerRefresh = playbackReady && !hasRecordingMedia;

  logWebsocketDiagnostic('meeting-meta', {
    meetingUuid,
    state: getMeetingMetaStateKey(metadata),
    isCurrentMeeting,
    playbackReady,
    hasRecordingMedia,
    becameReady
  });

  if (!isCurrentMeeting) {
    return;
  }

  // Drop the missing-media guard before any early return when:
  // - media has arrived, or
  // - the viewed meeting changed (A armed the key, B is non-ready and would
  //   otherwise exit without clearing — returning to A would then skip refresh).
  if (
    hasRecordingMedia ||
    (lastMissingMediaAudioRefreshKey &&
      !lastMissingMediaAudioRefreshKey.startsWith(`${meetingUuid}:`))
  ) {
    lastMissingMediaAudioRefreshKey = null;
  }

  if (!becameReady && !needsPlayerRefresh) {
    return;
  }

  let meetingFetch = Promise.resolve();
  if (becameReady || (needsPlayerRefresh && !recordingUrl)) {
    meetingFetch = Promise.resolve(
      store.dispatch(fetchCurrentlySelectedMeeting(meetingUuid))
    );
  }

  if (becameReady) {
    store.dispatch(setRefetchNeeded(true));
  }

  if (!playbackReady) {
    return;
  }

  const dispatchAudioPlayerRefresh = () => {
    // When playback is ready but media is still absent, meeting_meta can repeat
    // often — refresh the player at most once per meeting+recording URL until
    // media lands / meeting changes (guard cleared above) or the recording URL
    // changes.
    if (needsPlayerRefresh) {
      const refreshKey = missingMediaAudioRefreshKey(meetingUuid, recordingUrl);
      if (lastMissingMediaAudioRefreshKey === refreshKey) {
        return;
      }
      lastMissingMediaAudioRefreshKey = refreshKey;
    }
    store.dispatch({
      type: AudioPlayerActions.AUDIO_PLAYER_REFRESH,
      meetingUuid
    });
  };

  const refreshTranscript = currentMeeting => {
    const transcriptionUrl = currentMeeting?.transcription;
    const transcriptionId = transcriptionUrl
      ? getLastUrlParam(transcriptionUrl)
      : null;
    const transcription = transcriptionId
      ? store.getState().transcriptions?.data?.[transcriptionId]
      : null;

    // Skip while a force-refresh is already in flight: fetchTranscription(url, true)
    // clears editorState and sets fetching, so hasUsableTranscriptionContent stays
    // false and repeated playback-ready meeting_meta frames would otherwise stack
    // GETs / out-of-order overwrites.
    if (
      transcriptionUrl &&
      !transcription?.fetching &&
      !hasUsableTranscriptionContent(transcription)
    ) {
      store.dispatch(fetchTranscription(transcriptionUrl, true));
    }
  };

  if (recordingUrl) {
    dispatchAudioPlayerRefresh();
  }

  Promise.resolve(meetingFetch)
    .then(() => {
      const latestMeeting = store.getState().meetings?.data?.[meetingUuid];
      if (!recordingUrl && latestMeeting?.recording) {
        dispatchAudioPlayerRefresh();
      }
      refreshTranscript(latestMeeting);
    })
    .catch(() => undefined);
};

/**
 * A drop is invisible to the page: any `meeting_meta` published while the socket
 * was down is gone for good, and re-subscribing only delivers *future* frames.
 * Re-read the meeting over HTTP once and run it through the same handler so a
 * reconnect recovers the state we missed instead of waiting for a manual reload.
 */
const resyncCurrentMeetingAfterReconnect = store => {
  const meetingUuid = store.getState().app?.currentMeetingId;

  if (!meetingUuid) {
    return;
  }

  Promise.resolve(store.dispatch(fetchMeetingMeta(meetingUuid)))
    .then(() => store.dispatch(fetchCurrentlySelectedMeeting(meetingUuid)))
    .then(() => {
      refreshMeetingContentFromMeta(
        store,
        meetingUuid,
        store.getState().meetings?.metadata?.[meetingUuid]
      );
    })
    .catch(() => undefined);
};

const socketMiddleware = () => {
  let socket = null;
  let connecting = false;
  let processingSendMessage = false;
  /**
   * Every live `sub`, so a reconnect can restore them. The websocket reducer
   * drops its subscription map on DISCONNECTED and no component re-subscribes,
   * so this is the only surviving record of what the page is listening to.
   */
  const activeSubscriptions = new Map();
  let reconnectAttempt = 0;
  let reconnectTimeoutId = null;
  /** Suppresses reconnect for closes we asked for (logout, CONNECT churn). */
  let closedByClient = false;
  let pendingCopilotMessages = [];
  /** Queued when user triggers omnisearch while WS is not open (e.g. global Copilot before any chat send). */
  let pendingOmnisearchAction = null;
  /**
   * Same as {@link pendingOmnisearchAction} but for "Load more" page requests queued while the
   * socket is not open. A FIFO array (not a single slot) so multiple offline pages — e.g. "Load
   * more" on two different categories before the socket reopens — are all sent, in order, on
   * `onOpen`. This must stay in lockstep with the reducer's `contextOmnisearchLoadMorePendingKeys`
   * FIFO (each `SEND_COPILOT_OMNISEARCH_PAGE` pushes one key); dropping a queued send here without
   * dropping its key would misattribute a later page response to the wrong category.
   */
  let pendingOmnisearchPageActions = [];
  const durableCopilotConnectionsByRunUuid = {};
  /** Per-run chunk coalescers; see `helpers/copilotStreamChunkQueue`. */
  const durableCopilotChunkQueuesByRunUuid = {};
  const durableCopilotWatchdogsByRunUuid = {};
  const finishedDurableCopilotRunUuids = new Set();
  const pausedDurableCopilotRunUuids = new Set();
  const markDurableCopilotEventProcessed = createCopilotRunEventDeduper();

  const clearDurableCopilotWatchdog = runUuid => {
    const watchdogId = durableCopilotWatchdogsByRunUuid[runUuid];
    if (watchdogId) {
      clearTimeout(watchdogId);
      delete durableCopilotWatchdogsByRunUuid[runUuid];
    }
  };

  const finishDurableCopilotRun = (
    store,
    run,
    {
      status = 'failed',
      messages,
      errorMessage,
      showSystemMessage = true,
      dispatchTimeout = true
    } = {}
  ) => {
    if (!run?.runUuid || !run?.chatUuid) {
      return;
    }

    // Terminal handling reads the messages already in the store, so coalesced
    // chunks have to be applied first.
    durableCopilotChunkQueuesByRunUuid[run.runUuid]?.flush();

    const isAlreadyFinished = finishedDurableCopilotRunUuids.has(run.runUuid);
    const shouldReconcileFinishedCancellation =
      isAlreadyFinished && status === 'cancelled';
    if (isAlreadyFinished && !shouldReconcileFinishedCancellation) {
      return;
    }

    if (!isAlreadyFinished) {
      finishedDurableCopilotRunUuids.add(run.runUuid);
    }
    const activeRun =
      store.getState().copilot?.activeCopilotRunsByChatUuid?.[run.chatUuid];
    const finishedRun =
      activeRun?.runUuid === run.runUuid ? { ...run, ...activeRun } : run;
    const existingMessages =
      store.getState().copilot?.sessions?.data?.[finishedRun.chatUuid]
        ?.messages || [];
    const terminalMessages =
      status === 'cancelled'
        ? completeCopilotCancellationMessages(
            existingMessages,
            messages,
            finishedRun
          )
        : messages;

    if (isAlreadyFinished) {
      store.dispatch({
        type: CopilotActions.COPILOT_RUN_CANCEL_MESSAGES_RECEIVED,
        chatUuid: finishedRun.chatUuid,
        runUuid: finishedRun.runUuid,
        messages: terminalMessages,
        assistantMessageUuid: finishedRun.assistantMessageUuid,
        optimisticUserMessageClientRequestId:
          finishedRun.optimisticUserMessageClientRequestId
      });
      return;
    }

    clearDurableCopilotWatchdog(finishedRun.runUuid);
    removeStoredCopilotRun(finishedRun.chatUuid);

    store.dispatch({
      type: CopilotActions.COPILOT_RUN_FINISHED,
      chatUuid: finishedRun.chatUuid,
      runUuid: finishedRun.runUuid,
      status,
      messages: terminalMessages,
      assistantMessageUuid: finishedRun.assistantMessageUuid,
      optimisticUserMessageClientRequestId:
        finishedRun.optimisticUserMessageClientRequestId
    });

    if (showSystemMessage && errorMessage) {
      store.dispatch({
        type: CopilotActions.RECEIVE_COPILOT_MESSAGE,
        data: {
          uuid: null,
          author: MESSAGE_AUTHOR_TYPES.SYSTEM,
          message: errorMessage,
          chatUuid: finishedRun.chatUuid,
          created: new Date().toISOString()
        }
      });
    }

    if (dispatchTimeout) {
      store.dispatch({ type: CopilotActions.MESSAGE_TIMEOUT });
    }

    durableCopilotChunkQueuesByRunUuid[finishedRun.runUuid]?.clear();
    delete durableCopilotChunkQueuesByRunUuid[finishedRun.runUuid];
    durableCopilotConnectionsByRunUuid[finishedRun.runUuid]?.close();
    delete durableCopilotConnectionsByRunUuid[finishedRun.runUuid];
  };

  const startDurableCopilotChunkWatchdog = (store, run) => {
    if (!run?.runUuid || !run?.chatUuid) {
      return;
    }

    clearDurableCopilotWatchdog(run.runUuid);

    const delayMs = getCopilotChunkWatchdogDelayMs(run.startedAt);
    durableCopilotWatchdogsByRunUuid[run.runUuid] = setTimeout(() => {
      delete durableCopilotWatchdogsByRunUuid[run.runUuid];

      const activeRun =
        store.getState().copilot?.activeCopilotRunsByChatUuid?.[run.chatUuid];
      if (!activeRun || activeRun.runUuid !== run.runUuid) {
        return;
      }

      if (hasCopilotRunStreamProgress(activeRun)) {
        return;
      }

      finishDurableCopilotRun(store, run, {
        errorMessage: L10nCopilot.copilotNoReplyStartedTimeoutMessage
      });
    }, delayMs);
  };

  // Detaches from the chat's SSE stream without ending the run: the user
  // switched away, not gave up. A plain `.close()` fires the connection's
  // `onClose`, which — since the run is still `pending`/`running` in Redux —
  // treats this exactly like an unexpected drop and finishes it as
  // 'failed' (see `onClose` in connectDurableCopilotRun below), deleting it
  // from `activeCopilotRunsByChatUuid`/sessionStorage. That left nothing for
  // RECONNECT_COPILOT_SSE_RUN_FOR_CHAT to find when switching back, so the
  // stream state was gone until a full page refresh. `{ silent: true }`
  // closes the EventSource without running that finish-the-run side effect.
  const pauseDurableCopilotSseForChat = (store, chatUuid) => {
    const run =
      store.getState().copilot?.activeCopilotRunsByChatUuid?.[chatUuid] ||
      loadStoredCopilotRuns()[chatUuid];

    if (!run?.runUuid) {
      return;
    }

    clearDurableCopilotWatchdog(run.runUuid);
    pausedDurableCopilotRunUuids.add(run.runUuid);
    // Land anything already received so switching back does not lose the text
    // that arrived inside the last un-flushed window.
    durableCopilotChunkQueuesByRunUuid[run.runUuid]?.flush();
    delete durableCopilotChunkQueuesByRunUuid[run.runUuid];
    durableCopilotConnectionsByRunUuid[run.runUuid]?.close({ silent: true });
    delete durableCopilotConnectionsByRunUuid[run.runUuid];
  };

  const shouldUseCopilotSseStream = store =>
    shouldUseCopilotSseStreamForFlags(env.name, store.getState().user?.flags);

  const dispatchCopilotPayload = (
    store,
    payload,
    fallbackChatUuid,
    runUuid
  ) => {
    const normalizedPayload =
      payload && typeof payload === 'object' ? camelize(payload) : null;

    if (!normalizedPayload) {
      return;
    }

    const chatUuid = getCopilotStreamChatUuid(
      normalizedPayload,
      fallbackChatUuid
    );

    if (!chatUuid) {
      logError('Copilot stream payload missing chatUuid', {
        fallbackChatUuid,
        payloadMsg: normalizedPayload?.msg
      });
      return;
    }

    if (normalizedPayload?.msg === 'copilot_name_updated') {
      const nameData =
        normalizedPayload.data &&
        typeof normalizedPayload.data === 'object' &&
        !Array.isArray(normalizedPayload.data)
          ? camelize(normalizedPayload.data)
          : {};

      store.dispatch({
        type: CopilotActions.RECEIVE_COPILOT_NAME_UPDATE,
        data: {
          ...nameData,
          chatUuid
        }
      });
      return;
    }

    const messageData = getCopilotStreamMessageData(normalizedPayload);

    if (!messageData) {
      logError('Copilot stream payload missing message data', {
        chatUuid,
        payloadMsg: normalizedPayload?.msg
      });
      return;
    }

    store.dispatch({
      type: CopilotActions.RECEIVE_COPILOT_MESSAGE,
      data: {
        ...messageData,
        chatUuid,
        ...(runUuid ? { copilotRunUuid: runUuid } : {})
      }
    });
  };

  const upsertDurableCopilotRun = (store, run) => {
    store.dispatch({
      type: CopilotActions.COPILOT_RUN_STARTED,
      run
    });
    saveStoredCopilotRun(run);
  };

  const connectDurableCopilotRun = (store, run) => {
    if (!run?.runUuid || !run?.chatUuid) {
      logError('Copilot SSE connect skipped: missing run or chat uuid', {
        chatUuid: run?.chatUuid,
        runUuid: run?.runUuid
      });
      return;
    }

    if (durableCopilotConnectionsByRunUuid[run.runUuid]) {
      startDurableCopilotChunkWatchdog(store, run);
      return run;
    }

    let currentRun = run;
    const eventsUrl = buildCopilotRunEventsUrl(
      run.runUuid,
      currentRun.lastEventId
    );

    // React 17 has no automatic batching outside its own event handlers, so
    // every dispatch made from an SSE callback renders synchronously. Batch the
    // whole flush into one render, on top of the queue's chunk coalescing.
    const chunkQueue = createCopilotStreamChunkQueue({
      onFlush: chunks => {
        try {
          batch(() => {
            chunks.forEach(chunk => {
              dispatchCopilotPayload(
                store,
                chunk.payload,
                currentRun.chatUuid,
                currentRun.runUuid
              );
            });

            store.dispatch({
              type: CopilotActions.COPILOT_RUN_EVENT_RECEIVED,
              chatUuid: currentRun.chatUuid,
              runUuid: currentRun.runUuid,
              lastEventId: currentRun.lastEventId,
              assistantMessageUuid: currentRun.assistantMessageUuid,
              status: currentRun.status
            });
          });
        } catch (error) {
          logError('Copilot SSE chunk handler failed', {
            chatUuid: currentRun.chatUuid,
            runUuid: currentRun.runUuid,
            eventId: chunks[chunks.length - 1]?.meta?.id,
            errorMessage: error?.message
          });
        }
      }
    });
    durableCopilotChunkQueuesByRunUuid[run.runUuid] = chunkQueue;

    durableCopilotConnectionsByRunUuid[run.runUuid] = connectCopilotRun(
      run.runUuid,
      {
        lastEventId: currentRun.lastEventId,
        onOpen: () => {
          if (finishedDurableCopilotRunUuids.has(currentRun.runUuid)) {
            return;
          }
          finishedDurableCopilotRunUuids.delete(currentRun.runUuid);
          startDurableCopilotChunkWatchdog(store, currentRun);
        },
        onPayload: (payload, meta) => {
          if (!markDurableCopilotEventProcessed(currentRun.runUuid, meta.id)) {
            return;
          }

          try {
            clearDurableCopilotWatchdog(currentRun.runUuid);

            // Run progress is tracked per event even though rendering is
            // coalesced, so a reload/reconnect resumes from the newest event
            // instead of replaying chunks we already applied.
            currentRun = {
              ...currentRun,
              lastEventId: meta.id || currentRun.lastEventId,
              assistantMessageUuid:
                getCopilotStreamChunkMessageUuid(payload) ||
                currentRun.assistantMessageUuid,
              status: 'running'
            };
            saveStoredCopilotRun(currentRun);

            chunkQueue.enqueue({ payload, meta });
          } catch (error) {
            logError('Copilot SSE chunk handler failed', {
              chatUuid: currentRun.chatUuid,
              runUuid: currentRun.runUuid,
              eventId: meta.id,
              errorMessage: error?.message
            });
          }
        },
        onCompleted: (payload, meta) => {
          if (!markDurableCopilotEventProcessed(currentRun.runUuid, meta.id)) {
            return;
          }

          try {
            // Coalesced chunks must land before the terminal payload.
            chunkQueue.flush();

            if (payload) {
              dispatchCopilotPayload(
                store,
                payload,
                currentRun.chatUuid,
                currentRun.runUuid
              );
            }
            finishDurableCopilotRun(store, currentRun, {
              status: 'completed',
              showSystemMessage: false,
              dispatchTimeout: true
            });
          } catch (error) {
            logError('Copilot SSE completed handler failed', {
              chatUuid: currentRun.chatUuid,
              runUuid: currentRun.runUuid,
              eventId: meta.id,
              errorMessage: error?.message
            });
          }
        },
        onCancelled: (payload, meta) => {
          if (finishedDurableCopilotRunUuids.has(currentRun.runUuid)) {
            return;
          }
          if (!markDurableCopilotEventProcessed(currentRun.runUuid, meta.id)) {
            return;
          }

          chunkQueue.flush();

          const cancelledRun = normalizeCancelledRunPayload(
            payload,
            currentRun.runUuid
          );
          if (!cancelledRun) {
            logError('Copilot SSE cancelled event payload invalid', {
              chatUuid: currentRun.chatUuid,
              runUuid: currentRun.runUuid
            });
          }
          finishDurableCopilotRun(store, currentRun, {
            status: 'cancelled',
            messages: cancelledRun?.messages || [],
            showSystemMessage: false,
            dispatchTimeout: false
          });
        },
        onNameUpdated: (payload, meta) => {
          if (!markDurableCopilotEventProcessed(currentRun.runUuid, meta.id)) {
            return;
          }

          try {
            chunkQueue.flush();

            if (payload) {
              dispatchCopilotPayload(
                store,
                payload,
                currentRun.chatUuid,
                currentRun.runUuid
              );
            }

            const updatedRun = {
              ...currentRun,
              lastEventId: meta.id || currentRun.lastEventId
            };
            currentRun = updatedRun;
            store.dispatch({
              type: CopilotActions.COPILOT_RUN_EVENT_RECEIVED,
              chatUuid: currentRun.chatUuid,
              runUuid: currentRun.runUuid,
              lastEventId: updatedRun.lastEventId
            });
            saveStoredCopilotRun(updatedRun);
          } catch (error) {
            logError('Copilot SSE name_updated handler failed', {
              chatUuid: currentRun.chatUuid,
              runUuid: currentRun.runUuid,
              eventId: meta.id,
              errorMessage: error?.message
            });
          }
        },
        onError: errorEvent => {
          if (finishedDurableCopilotRunUuids.has(currentRun.runUuid)) {
            return;
          }

          chunkQueue.flush();

          const streamErrorMessage =
            (errorEvent &&
              typeof errorEvent === 'object' &&
              typeof errorEvent.message === 'string' &&
              errorEvent.message) ||
            COPILOT_ERROR_MESSAGE.message;

          logError('Copilot SSE stream error event', {
            chatUuid: currentRun.chatUuid,
            runUuid: currentRun.runUuid,
            eventsUrl,
            streamErrorMessage,
            hasPayload: Boolean(
              errorEvent && typeof errorEvent === 'object' && errorEvent.payload
            )
          });

          if (
            errorEvent &&
            typeof errorEvent === 'object' &&
            errorEvent.payload
          ) {
            try {
              dispatchCopilotPayload(
                store,
                errorEvent.payload,
                currentRun.chatUuid,
                currentRun.runUuid
              );
            } catch (error) {
              logError('Copilot SSE error payload dispatch failed', {
                chatUuid: currentRun.chatUuid,
                runUuid: currentRun.runUuid,
                errorMessage: error?.message
              });
            }
          }

          finishDurableCopilotRun(store, currentRun, {
            errorMessage: streamErrorMessage
          });
        },
        onNetworkError: event => {
          if (finishedDurableCopilotRunUuids.has(currentRun.runUuid)) {
            return;
          }

          chunkQueue.flush();

          logError('Copilot SSE network error', {
            chatUuid: currentRun.chatUuid,
            runUuid: currentRun.runUuid,
            eventsUrl,
            readyState: event?.target?.readyState
          });

          if (event?.target?.readyState === EventSource.CLOSED) {
            finishDurableCopilotRun(store, currentRun, {
              errorMessage: L10nCopilot.connectionLostErrorMessage
            });
          }
        },
        onClose: () => {
          chunkQueue.flush();

          if (finishedDurableCopilotRunUuids.has(currentRun.runUuid)) {
            return;
          }
          delete durableCopilotConnectionsByRunUuid[currentRun.runUuid];

          if (pausedDurableCopilotRunUuids.delete(currentRun.runUuid)) {
            return;
          }

          const activeRun =
            store.getState().copilot?.activeCopilotRunsByChatUuid?.[
              currentRun.chatUuid
            ];
          if (
            activeRun?.runUuid === currentRun.runUuid &&
            isActiveCopilotRunStatus(activeRun.status) &&
            !finishedDurableCopilotRunUuids.has(currentRun.runUuid)
          ) {
            finishDurableCopilotRun(store, currentRun, {
              showSystemMessage: false,
              dispatchTimeout: true
            });
          }
        }
      }
    );

    return run;
  };

  const beginDurableCopilotRun = (store, run) => {
    finishedDurableCopilotRunUuids.delete(run.runUuid);
    upsertDurableCopilotRun(store, run);
    connectDurableCopilotRun(store, run);
    return run;
  };

  const cancelDurableCopilotRun = async (store, runUuid) => {
    const activeRun = Object.values(
      store.getState().copilot?.activeCopilotRunsByChatUuid || {}
    ).find(run => run?.runUuid === runUuid);

    if (!activeRun) {
      return { runUuid, status: 'cancelled', messages: [] };
    }

    if (activeRun.isCancelPending) {
      return { runUuid, status: activeRun.status, messages: [] };
    }

    store.dispatch({
      type: CopilotActions.COPILOT_RUN_CANCEL_REQUESTED,
      chatUuid: activeRun.chatUuid,
      runUuid
    });

    try {
      const response = await cancelCopilotRunRequest(runUuid);
      finishDurableCopilotRun(store, activeRun, {
        status: response.status,
        messages: response.messages,
        showSystemMessage: false,
        dispatchTimeout: false
      });
      return response;
    } catch (error) {
      if (error?.status === 409) {
        store.dispatch({
          type: CopilotActions.FETCH_COPILOT_CHAT_DETAIL_REQUEST,
          uuid: activeRun.chatUuid
        });

        try {
          const response = await get(
            `${env.avomaApiUrl}/api/v1/copilot/chats/${activeRun.chatUuid}/`
          );
          const normalizedResponse = camelizeCopilotPayload(response);
          const chat = Array.isArray(normalizedResponse)
            ? normalizedResponse[0]
            : normalizedResponse;

          if (!chat?.uuid || chat.uuid !== activeRun.chatUuid) {
            throw new Error('Invalid copilot chat response');
          }

          if (!finishedDurableCopilotRunUuids.has(runUuid)) {
            store.dispatch({
              type: CopilotActions.FETCH_COPILOT_CHAT_DETAIL_SUCCESS,
              uuid: chat.uuid,
              chat
            });
          }
        } catch (reconcileError) {
          if (!finishedDurableCopilotRunUuids.has(runUuid)) {
            store.dispatch({
              type: CopilotActions.FETCH_COPILOT_CHAT_DETAIL_FAILURE,
              uuid: activeRun.chatUuid,
              error: reconcileError?.message
            });
          }
        }

        const terminalStatus =
          error?.data && typeof error.data === 'object'
            ? error.data.status
            : undefined;
        const reconciledStatus =
          terminalStatus === 'completed' ||
          terminalStatus === 'failed' ||
          terminalStatus === 'cancelled'
            ? terminalStatus
            : 'failed';

        finishDurableCopilotRun(store, activeRun, {
          status: reconciledStatus,
          messages:
            normalizeCancelledRunPayload(error?.data, runUuid)?.messages ||
            undefined,
          showSystemMessage: false,
          dispatchTimeout: true
        });
        return { status: reconciledStatus };
      }

      store.dispatch({
        type: CopilotActions.COPILOT_RUN_CANCEL_ROLLED_BACK,
        chatUuid: activeRun.chatUuid,
        runUuid
      });
      throw error;
    }
  };

  const sendDurableCopilotMessage = async (store, messageAction) => {
    const runPayload = {
      message: messageAction.message,
      data_json: getCopilotMessageDataJsonForAction(messageAction)
    };

    try {
      const runResponse = await startCopilotRun(
        messageAction.chatUuid,
        runPayload
      );
      const runUuid = getCopilotRunUuid(runResponse);

      if (!runUuid) {
        throw new Error('Missing Copilot run UUID');
      }

      const run = {
        chatUuid: messageAction.chatUuid,
        runUuid,
        optimisticUserMessageClientRequestId: `${runUuid}:optimistic-user`,
        status: runResponse.status || 'pending',
        startedAt: new Date().toISOString()
      };

      dispatchOptimisticCopilotUserMessage(
        store,
        messageAction,
        run.optimisticUserMessageClientRequestId,
        runUuid
      );
      return beginDurableCopilotRun(store, run);
    } catch (error) {
      const conflictRun =
        error?.name === HTTP_CONFLICT_ERROR_NAME || error?.status === 409
          ? camelize(error?.data || {})
          : null;
      const runUuid = getCopilotRunUuid(conflictRun);

      if (runUuid) {
        const run = {
          chatUuid: messageAction.chatUuid,
          runUuid,
          optimisticUserMessageClientRequestId: `${runUuid}:optimistic-user`,
          status: conflictRun.status || 'running',
          startedAt: new Date().toISOString()
        };
        dispatchOptimisticCopilotUserMessage(
          store,
          messageAction,
          run.optimisticUserMessageClientRequestId,
          runUuid
        );
        return beginDurableCopilotRun(store, run);
      }

      logError('Error starting Copilot stream run', {
        chatUuid: messageAction.chatUuid,
        errorMessage: error?.message,
        status: error?.status,
        response: error?.data
      });
      store.dispatch({ type: CopilotActions.MESSAGE_TIMEOUT });
      throw error;
    }
  };

  const clearReconnectTimer = () => {
    if (reconnectTimeoutId != null) {
      clearTimeout(reconnectTimeoutId);
      reconnectTimeoutId = null;
    }
  };

  const scheduleReconnect = store => {
    if (reconnectTimeoutId != null || activeSubscriptions.size === 0) {
      return;
    }

    const delay =
      WEBSOCKET_RECONNECT_DELAYS_MS[
        Math.min(reconnectAttempt, WEBSOCKET_RECONNECT_DELAYS_MS.length - 1)
      ];
    reconnectAttempt += 1;

    logWebsocketDiagnostic('reconnect-scheduled', {
      attempt: reconnectAttempt,
      delay,
      subscriptions: activeSubscriptions.size
    });

    reconnectTimeoutId = setTimeout(() => {
      reconnectTimeoutId = null;

      if (socket === null && !connecting) {
        connect(store);
      }
    }, delay);
  };

  const onOpen = store => event => {
    connecting = false;
    clearReconnectTimer();
    const isReconnect = reconnectAttempt > 0;
    reconnectAttempt = 0;

    store.dispatch({
      type: WebsocketActions.CONNECTED,
      host: event.target.url
    });

    if (socket?.readyState === WebSocket.OPEN) {
      // Covers both the first connect (subscriptions queued while the socket was
      // opening) and a reconnect, where the server has no record of us at all.
      activeSubscriptions.forEach(subscription => {
        socket.send(getMessageForAction(store, subscription, 'sub'));
        store.dispatch({
          ...subscription,
          type: WebsocketActions.SUBSCRIBED
        });
      });
    }

    logWebsocketDiagnostic('open', {
      isReconnect,
      subscriptions: activeSubscriptions.size
    });

    if (isReconnect) {
      resyncCurrentMeetingAfterReconnect(store);
    }

    if (
      pendingCopilotMessages.length > 0 &&
      socket?.readyState === WebSocket.OPEN
    ) {
      const messagesToProcess = [...pendingCopilotMessages];
      pendingCopilotMessages = [];

      messagesToProcess.forEach(messageAction => {
        try {
          socket.send(getMessageForAction(store, messageAction, 'send'));
        } catch (error) {
          logError('Error sending queued copilot message', error);
        }
      });
    }

    if (pendingOmnisearchAction && socket?.readyState === WebSocket.OPEN) {
      const omnisearchAction = pendingOmnisearchAction;
      pendingOmnisearchAction = null;
      try {
        socket.send(getOmnisearchSocketPayload(store, omnisearchAction));
      } catch (error) {
        logError('Error sending queued copilot omnisearch', error);
        store.dispatch({ type: CopilotActions.CLEAR_COPILOT_OMNISEARCH });
      }
    }

    if (
      pendingOmnisearchPageActions.length > 0 &&
      socket?.readyState === WebSocket.OPEN
    ) {
      const queuedOmnisearchPageActions = pendingOmnisearchPageActions;
      pendingOmnisearchPageActions = [];
      // Send in FIFO order so responses correlate to the reducer's pending-key queue.
      for (const omnisearchPageAction of queuedOmnisearchPageActions) {
        try {
          socket.send(getOmnisearchSocketPayload(store, omnisearchPageAction));
        } catch (error) {
          logError('Error sending queued copilot omnisearch page', error);
          // CLEAR empties the reducer's pending keys/buckets; stop flushing so we
          // don't send the remaining pages with nothing left to correlate them to.
          store.dispatch({
            type: CopilotActions.CLEAR_COPILOT_OMNISEARCH_PAGE
          });
          break;
        }
      }
    }
  };

  const onClose = (store, ownerSocket) => () => {
    // Ignore close events from sockets that are no longer current (CONNECT
    // replaced them). Otherwise a stale close can clear the new socket or
    // suppress reconnection via closedByClient.
    if (socket !== ownerSocket) {
      return;
    }

    connecting = false;

    if (pendingOmnisearchAction) {
      pendingOmnisearchAction = null;
      store.dispatch({ type: CopilotActions.CLEAR_COPILOT_OMNISEARCH });
    }

    if (pendingOmnisearchPageActions.length > 0) {
      pendingOmnisearchPageActions = [];
      store.dispatch({ type: CopilotActions.CLEAR_COPILOT_OMNISEARCH_PAGE });
    }

    store.dispatch({ type: WebsocketActions.DISCONNECTED });

    socket = null;

    logWebsocketDiagnostic('close', {
      closedByClient,
      subscriptions: activeSubscriptions.size
    });

    if (closedByClient) {
      closedByClient = false;
      return;
    }

    scheduleReconnect(store);
  };

  const onMessage = store => event => {
    const rawPayload = JSON.parse(event.data);
    // Copilot frames may carry an agent builder draft whose node configs must skip `camelize`.
    const payload =
      rawPayload?.stream === WebsocketActions.TYPE_COPILOT
        ? camelizeCopilotPayload(rawPayload)
        : camelize(rawPayload);

    switch (payload.stream) {
      case WebsocketActions.TYPE_BATTLECARDS:
        if (isEmpty(payload.data)) {
          logError(event.data, payload);
        } else {
          if (payload.data.dataType === 'answer_card') {
            store.dispatch({
              type: BattlecardActions.FETCH_BATTLECARD_SUCCESS,
              card: payload.data
            });

            // Track the event if the battlecard is triggered by the system
            store.dispatch(
              trackSegmentEvents({
                type: 'ANSWER_CARD_AUTO_DISPLAYED'
              })
            );
          }

          if (payload.data.dataType === 'rt_assistant') {
            store.dispatch({
              type: RealtimeAssistantActions.FETCH_REALTIME_ASSISTANT_SUCCESS,
              methodology: payload.data?.topicCovered
            });
          }
        }

        break;

      case WebsocketActions.TYPE_MEETING_REALTIME_TRANSCRIPTIONS:
        if (isEmpty(payload.data)) {
          // Filter out unnecessary messages from sentry logs
          // Do not log if the payload data is an empty object since that is not an error case
          if (payload.data === null || payload.data === undefined) {
            logError(event.data, payload);
          }
        } else if (payload.data.type === 'notes') {
          // Handle the translated notes here
          store.dispatch({
            type: MeetingTranslationActions.FETCH_MEETING_TRANSLATION_NOTES_SUCCESS,
            meetingUuid: payload.data.meetingUuid,
            data: payload.data
          });
        } else if (payload.data.type === 'transcript') {
          // Handle the translated transcript here
          store.dispatch({
            type: MeetingTranslationActions.FETCH_MEETING_TRANSLATION_TRANSCRIPT_SUCCESS,
            meetingUuid: payload.data.meetingUuid,
            data: payload.data
          });
        } else {
          // Handle real time transcript message here
          store.dispatch({
            type: RealtimeTranscriptFetchActions.FETCH_REALTIME_TRANSCRIPTION_SUCCESS,
            transcript: payload.data
          });
        }

        break;

      case WebsocketActions.TYPE_BOOKMARKS:
        if (isEmpty(payload.data)) {
          logError(event.data, payload);
        } else {
          store.dispatch({
            type: BookmarkActions.FETCH_BOOKMARK_SUCCESS,
            bookmark: payload.data
          });
        }

        break;

      case WebsocketActions.TYPE_MEETING_META:
        if (isEmpty(payload.dbObject)) {
          logError(event.data, payload);
        } else {
          if (payload.data?.scorecardUuid) {
            store.dispatch({
              type: ScorecardsActions.SET_SCORECARD_UUID_TO_FETCH,
              response: payload.data?.scorecardUuid
            });
          }

          if (payload.data?.recommendationUuid) {
            store.dispatch({
              type: CoachingActions.SET_COACHING_RECOMMENDATION_UUID_TO_FETCH,
              response: payload.data
            });
          }

          refreshMeetingContentFromMeta(
            store,
            payload.dbObject.uuid,
            payload.data
          );

          store.dispatch({
            type: MeetingActions.FETCH_META_SUCCESS,
            meetingId: payload.dbObject.uuid,
            metadata: payload.data
          });

          store.dispatch({
            type: MeetingActions.SET_ONLINE_USERS,
            meetingUuid: payload.dbObject.uuid,
            onlineUsers: payload.data.subscribedUsers
          });
        }

        break;

      case WebsocketActions.TYPE_COPILOT: {
        // Route to the primary grouped slot vs. a "Load more" page — see
        // `classifyCopilotOmnisearchResponse`.
        const copilotStateForOmnisearch = store.getState().copilot;
        const {
          isOmnisearchResponse,
          isPaginatedOmnisearchResponse,
          isOrphanedOmnisearchResponse
        } = classifyCopilotOmnisearchResponse(
          payload,
          copilotStateForOmnisearch
        );
        if (isOrphanedOmnisearchResponse) {
          break;
        }
        if (isPaginatedOmnisearchResponse) {
          store.dispatch({
            type: CopilotActions.RECEIVE_COPILOT_OMNISEARCH_PAGE,
            data: {
              requestId: readCopilotOmnisearchRequestIdFromWsData(payload.data),
              // Number (or null) so the reducer's `count`-based hasNext math works for a string count.
              count: readOmnisearchResultCount(payload.data?.count),
              // Not coerced to boolean: the backend's paginated response omits this field
              // entirely today, and the reducer distinguishes "explicitly false" from "absent"
              // (falls back to deriving hasNext from `count`) via `typeof ... === 'boolean'`.
              hasNext: payload.data?.hasNext,
              results: payload.data?.results
            }
          });
          break;
        }
        if (isOmnisearchResponse && payload.data) {
          const normalizedResults = normalizeCopilotOmnisearchResults(
            payload.data,
            payload.searchType ??
              payload.data?.searchType ??
              store.getState().copilot?.contextOmnisearch?.requestSearchType
          );
          store.dispatch({
            type: CopilotActions.RECEIVE_COPILOT_OMNISEARCH,
            data: {
              requestId: readCopilotOmnisearchRequestIdFromWsData(payload.data),
              ...normalizedResults
            }
          });
          break;
        }
        if (isEmpty(payload.dbObject)) {
          logError(event.data, payload);
        } else {
          dispatchCopilotPayload(store, payload);
        }

        break;
      }

      default:
        break;
    }
  };

  const connect = store => {
    connecting = true;

    const host = env.websocketUrl;

    // connect to the remote host
    socket = new WebSocket(host);
    const ownerSocket = socket;

    // websocket handlers — close is bound to this socket instance so a later
    // CONNECT replacement cannot be poisoned by the previous socket's close.
    socket.onmessage = onMessage(store);
    socket.onclose = onClose(store, ownerSocket);
    socket.onopen = onOpen(store);
  };

  // the middleware part of this function
  return store => next => action => {
    switch (action.type) {
      case WebsocketActions.CONNECT: {
        clearReconnectTimer();

        if (socket !== null) {
          closedByClient = true;
          socket.close();
        }

        // Reset immediately so a stale close from the previous socket cannot
        // leave closedByClient true and suppress reconnect/subscription replay
        // for the replacement socket. Stale closes are also ignored by owner
        // identity in onClose.
        closedByClient = false;

        // Always (re)start. An in-flight handshake leaves `connecting` true; if
        // we skipped connect() here, the aborted socket's later close would
        // clear `connecting` but scheduleReconnect no-ops when there are no
        // activeSubscriptions (Copilot-only / queued sends), leaving no live
        // socket. Duplicate CONNECT during handshake hits the same gap.
        connect(store);

        break;
      }

      case WebsocketActions.DISCONNECT:
        clearReconnectTimer();
        activeSubscriptions.clear();

        if (socket !== null) {
          closedByClient = true;
          socket.close();
        }

        socket = null;
        // A handshake that never opened leaves `connecting` set, and every later
        // SUBSCRIBE then declines to connect because one is "in flight".
        connecting = false;

        break;

      case WebsocketActions.SUBSCRIBE: {
        activeSubscriptions.set(getSubscriptionKey(action), action);

        if (socket && socket?.readyState === WebSocket.OPEN) {
          socket.send(getMessageForAction(store, action, 'sub'));
          store.dispatch({
            ...action,
            type: WebsocketActions.SUBSCRIBED
          });
        } else if (!connecting) {
          // `onOpen` replays everything in `activeSubscriptions`, so there is
          // nothing to queue separately.
          clearReconnectTimer();
          connect(store);
        }

        break;
      }

      case WebsocketActions.UNSUBSCRIBE: {
        activeSubscriptions.delete(getSubscriptionKey(action));

        if (socket && socket?.readyState === WebSocket.OPEN) {
          socket.send(getMessageForAction(store, action, 'unsub'));

          store.dispatch({
            ...action,
            type: WebsocketActions.UNSUBSCRIBED
          });
        } else if (!connecting) {
          connect(store);
        }

        break;
      }

      case CopilotActions.CANCEL_COPILOT_RUN: {
        const cancellationPromise = cancelDurableCopilotRun(
          store,
          action.runUuid
        );
        next(action);
        return cancellationPromise;
      }

      case CopilotActions.SEND_COPILOT_MESSAGE: {
        if (shouldUseCopilotSseStream(store)) {
          const durableRunPromise = sendDurableCopilotMessage(store, action);
          next(action);
          return durableRunPromise;
        }

        const processCopilotMessage = messageAction => {
          if (!socket || socket?.readyState !== WebSocket.OPEN) {
            pendingCopilotMessages.push(messageAction);
            return;
          }

          processingSendMessage = true;

          try {
            socket.send(getMessageForAction(store, messageAction, 'send'));
          } catch (error) {
            logError('Error sending receiving message from copilot', error);
          } finally {
            processingSendMessage = false;

            if (pendingCopilotMessages.length > 0) {
              const nextMessage = pendingCopilotMessages.shift();
              processCopilotMessage(nextMessage);
            }
          }
        };

        if (processingSendMessage) {
          pendingCopilotMessages.push(action);
        } else {
          processCopilotMessage(action);
        }
        break;
      }

      case CopilotActions.SEND_COPILOT_OMNISEARCH: {
        // A fresh primary search supersedes any queued "Load more": the reducer clears the
        // pending-page queue for this action, so replaying a queued page on connect would
        // produce a response with nothing to correlate it to (and, worse, could be mistaken
        // for the primary response — see `isPaginatedOmnisearchResponse`).
        pendingOmnisearchPageActions = [];
        if (socket && socket.readyState === WebSocket.OPEN) {
          try {
            socket.send(getOmnisearchSocketPayload(store, action));
          } catch (error) {
            logError('Error sending copilot omnisearch', error);
            return next({ type: CopilotActions.CLEAR_COPILOT_OMNISEARCH });
          }
        } else {
          pendingOmnisearchAction = action;
          if (!connecting) {
            connect(store);
          }
        }
        return next(action);
      }

      case CopilotActions.SEND_COPILOT_OMNISEARCH_PAGE: {
        if (socket && socket.readyState === WebSocket.OPEN) {
          try {
            socket.send(getOmnisearchSocketPayload(store, action));
          } catch (error) {
            logError('Error sending copilot omnisearch page', error);
            return next({ type: CopilotActions.CLEAR_COPILOT_OMNISEARCH_PAGE });
          }
        } else {
          pendingOmnisearchPageActions.push(action);
          if (!connecting) {
            connect(store);
          }
        }
        return next(action);
      }

      case CopilotActions.CLEAR_COPILOT_OMNISEARCH: {
        pendingOmnisearchAction = null;
        return next(action);
      }

      case CopilotActions.CLEAR_COPILOT_OMNISEARCH_PAGE: {
        pendingOmnisearchPageActions = [];
        return next(action);
      }

      case CopilotActions.RECONNECT_COPILOT_RUNS: {
        if (!shouldUseCopilotSseStream(store)) {
          break;
        }

        const storedRuns = loadStoredCopilotRuns();
        store.dispatch({
          type: CopilotActions.REHYDRATE_COPILOT_RUNS,
          runs: storedRuns
        });
        Object.values(storedRuns).forEach(run => {
          if (run.status === 'pending' || run.status === 'running') {
            connectDurableCopilotRun(store, run);
          }
        });
        break;
      }

      case CopilotActions.DISCONNECT_COPILOT_SSE_RUN_FOR_CHAT: {
        if (!shouldUseCopilotSseStream(store) || !action.chatUuid) {
          break;
        }

        pauseDurableCopilotSseForChat(store, action.chatUuid);
        break;
      }

      case CopilotActions.RECONNECT_COPILOT_SSE_RUN_FOR_CHAT: {
        if (!shouldUseCopilotSseStream(store) || !action.chatUuid) {
          break;
        }

        const run =
          store.getState().copilot?.activeCopilotRunsByChatUuid?.[
            action.chatUuid
          ] || loadStoredCopilotRuns()[action.chatUuid];

        if (run && isActiveCopilotRunStatus(run.status)) {
          connectDurableCopilotRun(store, run);
        }
        break;
      }

      default:
        return next(action);
    }

    return next(action);
  };
};

export { refreshMeetingContentFromMeta };

export default socketMiddleware();
